/**
 * Feb 2026 R7 · Requirements Gathering (Questionnaires) — list page.
 *
 * Shows every non-template requirement brief in the workspace. AI
 * generation happens through a dialog (prompt + optional reference URLs
 * + optional attachment id list).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Sparkles, Plus, Send, Link as LinkIcon, MessageSquare } from "lucide-react";

const STYLES = [
  { value: "balanced", label: "Balanced (creative + tech + budget)" },
  { value: "creative", label: "Creative discovery" },
  { value: "technical", label: "Technical scope" },
  { value: "budgetary", label: "Budget & timeline" },
];

export default function Questionnaires() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [titleHint, setTitleHint] = useState("");
  const [style, setStyle] = useState("balanced");
  const [maxQ, setMaxQ] = useState(10);
  const [urls, setUrls] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/requirement-briefs`),
        api.get(`/workspaces/${activeWorkspaceId}/requirement-brief-templates`),
      ]);
      setRows(Array.isArray(r1.data) ? r1.data : []);
      setTemplates(Array.isArray(r2.data) ? r2.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const runGenerate = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt"); return; }
    setGenerating(true);
    try {
      const parsed = urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/generate`,
        { prompt: prompt.trim(),
          title_hint: titleHint.trim() || null,
          style, max_questions: Number(maxQ),
          reference_urls: parsed },
      );
      toast.success(`Generated ${data.questions?.length || 0} questions`);
      setDialogOpen(false);
      setPrompt(""); setTitleHint(""); setUrls("");
      window.location.href = `/app/requirement-briefs/${data.id}`;
    } catch (e) { toast.error(apiErrorMessage(e, "AI generation failed")); }
    finally { setGenerating(false); }
  };

  const fromTemplate = async (tplId) => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/from-template`,
        { template_id: tplId },
      );
      toast.success("Draft created from template");
      window.location.href = `/app/requirement-briefs/${data.id}`;
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create from template")); }
  };

  return (
    <div className="min-h-screen" data-testid="requirement-briefs-page">
      <PageHeader
        title="Requirements gathering"
        description="AI-generated questionnaires you can share with clients to scope any brief before quoting."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-none" data-testid="rb-templates-btn">
              <Link to="/app/requirement-briefs/templates">Templates</Link>
            </Button>
            <Button onClick={() => setDialogOpen(true)} className="rounded-none" data-testid="rb-generate-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Generate with AI
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-8">
        {templates.length > 0 && (
          <section>
            <div className="label-eyebrow mb-2">Template library</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.slice(0, 6).map((t) => (
                <button
                  key={t.id}
                  onClick={() => fromTemplate(t.id)}
                  className="border border-border p-3 text-left hover:bg-muted/40"
                  data-testid={`rb-tpl-${t.id}`}
                >
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {t.question_count} questions · used {t.usage_count} times
                    {t.industry ? ` · ${t.industry}` : ""}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
        <section>
          <div className="label-eyebrow mb-2">Your questionnaires</div>
          {busy && rows.length === 0 && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {!busy && rows.length === 0 && (
            <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="rb-empty">
              No questionnaires yet. Click <b>Generate with AI</b> to create your first one from a prompt.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((r) => (
              <Link
                key={r.id}
                to={`/app/requirement-briefs/${r.id}`}
                className="border border-border p-3 hover:bg-muted/40 block"
                data-testid={`rb-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{r.title}</div>
                    {r.description && (
                      <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                    )}
                  </div>
                  <span className={`chip text-[9px] ${
                    r.status === "published" ? "chip-approved" :
                    r.status === "draft" ? "chip-pending" : "chip-status"
                  }`}>{r.status}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-3">
                  <span>{r.question_count || 0} Q</span>
                  <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{r.response_count || 0}</span>
                  <span>· iter {r.iteration || 1}</span>
                  {r.target_type !== "standalone" && (
                    <span className="chip text-[9px] chip-internal">{r.target_type}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="rb-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate questionnaire with AI</DialogTitle>
            <DialogDescription>
              Describe the project — Claude drafts questions and suggested answers you can review, refine, and share.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">What are you scoping? (required)</Label>
              <Textarea
                value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="E.g., E-commerce redesign for a mid-market fashion label targeting 25-35 women, Shopify + Instagram commerce, 3-month timeline"
                rows={5}
                className="text-sm rounded-none"
                data-testid="rb-generate-prompt"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Title hint (optional)</Label>
                <Input
                  value={titleHint} onChange={(e) => setTitleHint(e.target.value)}
                  placeholder="Fashion brand relaunch"
                  className="h-9 rounded-none"
                  data-testid="rb-generate-title-hint"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Style</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="rb-generate-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STYLES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Max questions</Label>
                <Input
                  type="number" min={3} max={30}
                  value={maxQ} onChange={(e) => setMaxQ(e.target.value)}
                  className="h-9 rounded-none"
                  data-testid="rb-generate-maxq"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Reference URLs (one per line)</Label>
                <Textarea
                  value={urls} onChange={(e) => setUrls(e.target.value)}
                  placeholder="https://competitor.com/about"
                  rows={2}
                  className="text-xs rounded-none"
                  data-testid="rb-generate-urls"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={runGenerate} disabled={generating || !prompt.trim()}
              className="rounded-none"
              data-testid="rb-generate-submit"
            >
              {generating ? "Generating…" : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
